import {
  GridTable,
  GridTableColumn,
  GridTableRecord,
} from "@apex/component-library";
import { Box, Stack } from "@chakra-ui/react";
import { useLocalStorage } from "ui-classic-common-library";

import { RequirementsResponse } from "../../../api/useGetRequirements";
import { csvExportAndDownload } from "../../../utils/csvExportAndDownload";
import { Currency, formatCurrency } from "../../../utils/formatCurrency";
import getDateString from "../../../utils/getDateString";
import { getEnumAsFilterOptions } from "../../CallsAlertsResultsGrid/gridUtils";
import { marginProcessingExportFileName } from "../auxUtils";
import { SectionLoadingState } from "../RenderState";
import { RequirementTypes } from "./types";

interface SummariesTableProps {
  data?: RequirementsResponse;
  isLoading: boolean;
}

const colWidth = 137.5;

const SummariesTable = ({ data, isLoading }: SummariesTableProps) => {
  const totalRequirement = data?.requirementsSummaries
    ? formatCurrency(
        Currency.Usd,
        String(
          Math.round(
            data?.requirementsSummaries?.reduce(
              (acc, curr) => acc + curr.totalRequirement,
              0
            ) * 100
          ) / 100
        )
      )
    : 0;

  const viewByClassColumns: GridTableColumn[] = [
    {
      key: "tradeType",
      label: "Trade Type",
      recordField: "type",
      isSortable: true,
      width: colWidth,
      isFilterable: true,
      isVisibleByDefault: true,
      options: getEnumAsFilterOptions(RequirementTypes),
    },
    {
      key: "class",
      label: "Class",
      recordField: "class",
      isSortable: true,
      width: colWidth - 1,
      isFilterable: true,
      isVisibleByDefault: true,
    },
    {
      key: "requirement",
      label: "Requirement",
      recordField: "requirement",
      isSortable: true,
      alignment: "right",
      width: colWidth,
      isFilterable: true,
      isVisibleByDefault: true,
    },
    {
      key: "processDate",
      label: "Process Date",
      recordField: "processDate",
      isSortable: true,
      width: colWidth,
      isFilterable: true,
      isVisibleByDefault: true,
      filterByDate: true,
    },
  ];

  const defaultVisibleOptions: string[] = [
    "type",
    "class",
    "processDate",
    "requirement",
  ];

  const [visibleColumns, setVisibleColumns] = useLocalStorage(
    "MARGIN_PROCESSING",
    "requirements-and-activity-by-class",
    Array.from(defaultVisibleOptions)
  );

  const tableData = (): GridTableRecord[] => {
    if (data) {
      return data.requirementsSummaries.map((data, index) => {
        return {
          key: `summaries-${index}`,
          type: data.type,
          class: data.reqClass,
          requirement: formatCurrency(
            Currency.Usd,
            data.totalRequirement ? String(data.totalRequirement) : "0"
          ),
          processDate: getDateString(data.processDate),
        };
      });
    }
    return [];
  };

  return (
    <>
      {isLoading && <SectionLoadingState />}
      {!isLoading && (
        <Stack spacing={0}>
          <GridTable
            columns={viewByClassColumns}
            records={tableData()}
            hasFooter={false}
            minHeight="auto"
            initialVisibleColumns={new Set(visibleColumns)}
            onChangeVisibleColumns={updatedColumns =>
              setVisibleColumns(Array.from(updatedColumns))
            }
            tableBorder={false}
            exportFileName={marginProcessingExportFileName()}
            data-testid="summaries-table"
            onExportClick={csvExportAndDownload}
          />
          {data?.requirementsSummaries &&
            data?.requirementsSummaries.length > 0 && (
              <Box
                border={1}
                borderStyle="solid"
                borderColor="gray.200"
                width={colWidth * 4 - 1}
                background="gray.100"
                py={2}
              >
                <Box
                  fontWeight={700}
                  fontSize="small"
                  width={colWidth * 3 - 10.5}
                  textAlign="right"
                >
                  {`Total: ${totalRequirement}`}
                </Box>
              </Box>
            )}
        </Stack>
      )}
    </>
  );
};

export default SummariesTable;
